import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { ShoppingBag, Pencil, Trash2 } from 'lucide-react';
import type { Sale, SaleCreateInput, SaleUpdateInput } from 'shared/schemas';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { EmptyStateV3 } from '../../../components/layout/EmptyStateV3.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { CancelSaleDialog } from '../../../features/sales/components/CancelSaleDialog.tsx';
import { MarkSalePaidDialog } from '../../../features/sales/components/MarkSalePaidDialog.tsx';
import { SaleDeleteDialog } from '../../../features/sales/components/SaleDeleteDialog.tsx';
import { SaleDetail } from '../../../features/sales/components/SaleDetail.tsx';
import { SaleForm } from '../../../features/sales/components/SaleForm.tsx';
import { SalesKanban, type SaleStatus } from '../../../features/sales/components/SalesKanban.tsx';
import { SaleStatusBadge } from '../../../features/sales/components/SaleStatusBadge.tsx';
import { useSales } from '../../../features/sales/hooks/useSales.ts';
import { useSalesStats } from '../../../features/sales/hooks/useSalesStats.ts';
import { useCreateSale } from '../../../features/sales/hooks/useCreateSale.ts';
import { useUpdateSale } from '../../../features/sales/hooks/useUpdateSale.ts';
import { STRINGS } from '../../../features/sales/sales.strings.ts';

const salesSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/sales/')({
  validateSearch: (s) => salesSearchSchema.parse(s),
  component: SalesListPage,
});

function SalesListPage() {
  const navigate = useNavigate();
  const [saleToDelete, setSaleToDelete] = useState<Sale | null>(null);
  const [saleToMarkPaid, setSaleToMarkPaid] = useState<Sale | null>(null);
  const [saleToCancel, setSaleToCancel] = useState<Sale | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const { data, isLoading } = useSales({ page: 1, pageSize: 100 });
  const stats = useSalesStats();
  const createSale = useCreateSale();
  const updateSale = useUpdateSale(editId ?? '');

  const sales = data?.data ?? [];
  const selectedSale = sales.find((s) => s.id === selectedId) ?? null;
  const editSale = editId ? sales.find((s) => s.id === editId) : undefined;

  function openNew() {
    void navigate({ to: '/sales', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/sales', search: {}, replace: true });
  }

  function handleCreate(formData: SaleCreateInput) {
    createSale.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(formData: SaleUpdateInput) {
    updateSale.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleMoveToStatus(sale: Sale, _status: SaleStatus) {
    // For non-special transitions (currently only pending → draft which doesn't exist)
    // Simply show a toast indicating DnD is not supported for this transition
    toast.info(`Movimiento a ${_status} no está disponible desde aquí.`);
  }

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <Button type="button" onClick={openNew}>
            {STRINGS.NEW_BUTTON}
          </Button>
        }
      />

      <KpiStrip>
        <KpiCard label="Total" value={String(stats.total)} />
        <KpiCard label={STRINGS.STATUS_PAID} value={String(stats.cobradas)} />
        <KpiCard label={STRINGS.STATUS_PENDING} value={String(stats.pendientes)} />
        <KpiCard label="Pendientes +30d" value={String(stats.pendientesMas30d)} />
      </KpiStrip>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : sales.length === 0 ? (
          <EmptyStateV3
            icon={ShoppingBag}
            title="Sin ventas"
            description={STRINGS.LIST_EMPTY}
            action={
              <Button type="button" onClick={openNew}>
                {STRINGS.NEW_BUTTON}
              </Button>
            }
          />
        ) : (
          <SalesKanban
            sales={sales}
            onSelectSale={(sale) =>
              void navigate({ to: '/sales', search: { view: sale.id }, replace: true })
            }
            onMoveToPaid={(sale) => setSaleToMarkPaid(sale)}
            onMoveToCancelled={(sale) => setSaleToCancel(sale)}
            onMoveToStatus={handleMoveToStatus}
          />
        )}
      </div>

      {/* Sale detail sheet */}
      <Sheet
        open={Boolean(selectedId)}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <SheetContent side="right" className="w-full max-w-lg overflow-hidden p-0">
          {selectedSale && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <SaleStatusBadge status={selectedSale.status} />
                    <SheetTitle className="truncate text-base">
                      {selectedSale.clientName ?? selectedSale.landName ?? STRINGS.DETAIL_TITLE}
                    </SheetTitle>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void navigate({ to: '/sales', search: { edit: selectedSale.id }, replace: true })
                      }
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setSaleToDelete(selectedSale)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <div className="overflow-y-auto p-6">
                <SaleDetail
                  sale={selectedSale}
                  onMarkPaid={() => setSaleToMarkPaid(selectedSale)}
                  onCancel={() => setSaleToCancel(selectedSale)}
                  onDelete={() => setSaleToDelete(selectedSale)}
                  onEdit={() =>
                    void navigate({ to: '/sales', search: { edit: selectedSale.id }, replace: true })
                  }
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New sale dialog */}
      <Dialog
        open={isNewOpen === true}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <SaleForm
              mode="create"
              formId="sale-create-form"
              onSubmit={handleCreate}
              isPending={createSale.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="sale-create-form" disabled={createSale.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit sale dialog */}
      <Dialog
        open={editSale !== null && editSale !== undefined}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editSale && (
            <>
              <DialogBody>
                <SaleForm
                  mode="edit"
                  formId="sale-edit-form"
                  currentStatus={editSale.status}
                  initialValues={editSale}
                  onSubmit={handleUpdate}
                  isPending={updateSale.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="sale-edit-form" disabled={updateSale.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <SaleDeleteDialog
        sale={saleToDelete}
        open={saleToDelete !== null}
        onClose={() => {
          setSaleToDelete(null);
          closeModals();
        }}
      />

      {/* Mark paid dialog */}
      <MarkSalePaidDialog
        sale={saleToMarkPaid}
        open={saleToMarkPaid !== null}
        onClose={() => setSaleToMarkPaid(null)}
      />

      {/* Cancel dialog */}
      {saleToCancel && (
        <CancelSaleDialog
          saleId={saleToCancel.id}
          open={true}
          onClose={() => setSaleToCancel(null)}
        />
      )}
    </div>
  );
}
